// ============================================================
// OrderRail - the turn order, the roster and the target picker in one
// strip above the map (v8.317.0 as a column beside it; the strip since
// plan design-gaps L2, the only layout since v12.0.0).
//
// It replaces TWO bars: the turn tracker's chip row and the roster's
// tab bar, which showed the same people twice in two orders. Every
// combatant is one row - band (team livery), initiative, name, marks,
// health - and ONE CLICK ON A ROW TARGETS IT. Rows in the order come
// first, in tracker order; the rest sit under a divider. With the
// tracker off the rail is simply the roster.
//
// THE E2E CONTRACT, kept on purpose so forty specs moved rather than
// being rewritten: the rows are a `tablist` named "Target tabs" of
// `role="tab"` rows; a tracked row carries `turn-chip-<participant>`,
// `data-status`, its `turn-initiative-<participant>` input and
// `turn-reroll-<participant>`; the tracker's chrome (round badge, the
// controls, END TURN, the empty message) lives in an element whose class
// contains "trackerBar" and which exists ONLY while the tracker is on -
// `trackerBar(page)` in e2e/helpers.js finds it by that class, and the
// toggle spec asserts it disappears. The close affordance is the same
// 'x' text the tab bar had; "+" in the tablist is New Tab.
// ============================================================
import { memo, useCallback, useState } from 'react';
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import clsx from '../utils/clsx';
import CohesionMeters from './killTeam/CohesionMeter';
import styles from '../styles/OrderRail.module.css';
import OrderRow from './OrderRow';
import { useTargets } from '../contexts/TargetsContext';
import { useSession } from '../contexts/SessionContext';
import { actingRow } from '../engine/roster/orderRail';
import type { RailRow } from '../engine/roster/orderRail';
import type { TurnTrackerState, TurnOrderMode } from '../types';
import { rovingIndex } from '../utils/rovingIndex';
import useStripOverflow from '../hooks/useStripOverflow';
import { useStableHandlers } from '../hooks/useStableCallback';
import {
  useCustomDraft,
  TrackerHead,
  ManualOrderNote,
  JoinHint,
  EndTurnFoot,
  emptyMessage,
  orderFacts,
  RailFrame,
} from './OrderRailChrome';
import type { CustomDraft } from './OrderRailChrome';

/** The slice of the targets context the rail uses - the context itself is untyped JSX. */
interface TargetsApi {
  activeTab: string | null;
  setActiveTab: (id: string) => void;
  renamingTab: string | null;
  setRenamingTab: (id: string | null) => void;
  dragTab: string | null;
  setDragTab: (id: string | null) => void;
  setTabCtx: (ctx: { id: string; x: number; y: number } | null) => void;
  handleTabDrop: (from: string, to: string) => void;
  handleNewTab: () => void;
  handleCloseTab: (id: string) => void;
  handleRenameTab: (id: string, name: string) => void;
}

export interface OrderRailProps {
  rows: RailRow[];
  trackerState: TurnTrackerState;
  /** The turn-tracker lock: freezes the order's controls, never the targeting. */
  locked: boolean;
  onEndTurn: () => void;
  onTogglePause: () => void;
  onSetInitiative: (id: string, value: number | null) => void;
  onRemoveParticipant: (id: string) => void;
  onReorderParticipants: (fromIdx: number, toIdx: number) => void;
  onAddCustom: (name: string) => void;
  /**
   * Admit every roster member not yet in the order, in one press
   * (v8.332.0). Null when there is nobody to admit, and the button goes
   * with it - the head shows only what can be done.
   */
  onTrackEveryone?: (() => void) | null;
  onResetCombat: (keepParticipants: boolean) => void;
  onReroll: (id: string) => void;
  onRerollAll: () => void;
  onRerollOthers: () => void;
  onDismissJoinHint: () => void;
  onSetOrderMode: (mode: TurnOrderMode) => void;
  showDialog: (type: string, message: string, defaultValue?: unknown) => Promise<unknown>;
  /** The combatant under the pointer, on the map or here (v8.319.0). */
  hoveredId?: string | null;
  onHoverRow?: (id: string | null) => void;
  /** My actor's pips are all spent: END TURN lights, and never presses itself. */
  endSuggested?: boolean;
}

type Participants = NonNullable<TurnTrackerState['participants']>;

interface OrderDrag {
  from: number;
  over: number | null;
}

/** Where an order drag lands, or null when it went nowhere new. */
function dropIndex(drag: OrderDrag | null): number | null {
  if (!drag || drag.over === null || drag.over === drag.from) return null;
  return drag.over;
}

/** Ending either drag: an order drag that moved reorders the order; both drags clear. */
function useDragEnd(
  orderDrag: OrderDrag | null,
  setOrderDrag: (drag: OrderDrag | null) => void,
  setDragTab: (id: string | null) => void,
  onReorderParticipants: OrderRailProps['onReorderParticipants'],
) {
  return useCallback(() => {
    const to = dropIndex(orderDrag);
    if (orderDrag && to !== null) onReorderParticipants(orderDrag.from, to);
    setOrderDrag(null);
    setDragTab(null);
  }, [orderDrag, setOrderDrag, onReorderParticipants, setDragTab]);
}

/** A roster row (outside the order) that a dragged roster tab can be dropped on. */
function isRosterDropTarget(row: RailRow, dragTab: string | null): boolean {
  return !!dragTab && !row.inOrder && !!row.combatantId && row.combatantId !== dragTab;
}

/**
 * Two drags on one rail. A row IN the order reorders the order (manual
 * mode, or before anyone has rolled); a row outside it reorders the
 * roster, which is what the tab bar's drag always did.
 */
function useRailDrag(
  participants: Participants,
  targets: TargetsApi,
  onReorderParticipants: OrderRailProps['onReorderParticipants'],
) {
  const { dragTab, setDragTab, handleTabDrop } = targets;
  const [orderDrag, setOrderDrag] = useState<OrderDrag | null>(null);
  const onDragStart = useCallback(
    (row: RailRow, _index: number) => {
      if (row.inOrder) setOrderDrag({ from: participants.findIndex((p) => p.id === row.participantId), over: null });
      else if (row.combatantId) setDragTab(row.combatantId);
    },
    [setDragTab, participants],
  );
  const onDragOver = useCallback(
    (row: RailRow) => {
      if (orderDrag && row.inOrder)
        setOrderDrag({ ...orderDrag, over: participants.findIndex((p) => p.id === row.participantId) });
      else if (isRosterDropTarget(row, dragTab)) handleTabDrop(dragTab!, row.combatantId!);
    },
    [orderDrag, dragTab, handleTabDrop, participants],
  );
  const onDragEnd = useDragEnd(orderDrag, setOrderDrag, setDragTab, onReorderParticipants);
  return { onDragStart, onDragOver, onDragEnd };
}

/**
 * Roving focus over the TARGETABLE rows: up and down walk the rail, and
 * the tab bar's left and right keep working for anyone used to them.
 */
function useRailKeys(rows: RailRow[], activeTab: string | null, setActiveTab: (id: string) => void) {
  const targetable = rows.filter((r) => r.combatantId);
  return useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const ids = targetable.map((r) => r.combatantId!);
      const next = rovingIndex(e.key, ids.indexOf(activeTab ?? ''), ids.length);
      if (next === null) return;
      e.preventDefault();
      setActiveTab(ids[next]);
      const el = e.currentTarget.parentElement?.querySelector<HTMLElement>(`[data-combatant-id="${ids[next]}"]`);
      // The strip keeps the target in view clear of its fades (useStripOverflow): focus must not scroll it first.
      el?.focus({ preventScroll: true });
    },
    [targetable, activeTab, setActiveTab],
  );
}

/** Everything a row needs from the rail, bar the row itself. */
interface RowContext {
  activeTab: string | null;
  hoveredId: string | null;
  locked: boolean;
  manageLocked: boolean;
  orderDraggable: boolean;
  renamingTab: string | null;
  handlers: Pick<
    Parameters<typeof OrderRow>[0],
    | 'onSelect'
    | 'onKeyNav'
    | 'onRenameStart'
    | 'onRenameCommit'
    | 'onRenameCancel'
    | 'onContext'
    | 'onClose'
    | 'onSetInitiative'
    | 'onReroll'
    | 'onDragStart'
    | 'onDragOver'
    | 'onDragEnd'
    | 'onHover'
  >;
}

/** Whether `id` is set and is the one given - a custom row has no combatant to match. */
const isId = (id: string | null | undefined, other: string | null) => !!id && id === other;

function RailRowSlot({ row, index, ctx, divider }: { row: RailRow; index: number; ctx: RowContext; divider: boolean }) {
  return (
    <div className={styles.rowSlot}>
      {divider && (
        <div className={styles.divider} data-testid="rail-divider">
          Not in the order
        </div>
      )}
      <OrderRow
        row={row}
        index={index}
        selected={isId(row.combatantId, ctx.activeTab)}
        hovered={isId(row.combatantId, ctx.hoveredId)}
        locked={ctx.locked}
        manageLocked={ctx.manageLocked}
        draggable={row.inOrder ? ctx.orderDraggable : !ctx.manageLocked}
        renaming={isId(row.combatantId, ctx.renamingTab)}
        {...ctx.handlers}
      />
    </div>
  );
}

function NewTabButton({ manageLocked, onNew }: { manageLocked: boolean; onNew: () => void }) {
  return (
    <button
      type="button"
      className={clsx(styles.newTabBtn, manageLocked && styles.locked)}
      onClick={onNew}
      title="New Tab (N)"
    >
      +{manageLocked ? <span className={styles.lockIcon}>{'🔒'}</span> : null}
    </button>
  );
}

/** The tablist: every row, the divider before the first row outside the order, and New Tab. */
function RailRows({
  rows,
  ctx,
  active,
  emptyMessage,
  onNewTab,
  scroll,
}: {
  rows: RailRow[];
  ctx: RowContext;
  active: boolean;
  emptyMessage: string | null;
  onNewTab: () => void;
  /** The strip's scroller (useStripOverflow): its ref, and which sides hide seats - the stylesheet fades them. */
  scroll: ReturnType<typeof useStripOverflow>;
}) {
  const firstOutside = rows.findIndex((r) => !r.inOrder);
  const showDivider = active && firstOutside > 0;
  const { overflow, atStart, atEnd } = scroll.state;
  return (
    <div
      ref={scroll.scrollerRef}
      className={styles.rows}
      role="tablist"
      aria-label="Target tabs"
      data-more-start={(overflow && !atStart) || undefined}
      data-more-end={(overflow && !atEnd) || undefined}
    >
      {rows.map((row, i) => (
        <RailRowSlot key={row.key} row={row} index={i} ctx={ctx} divider={showDivider && i === firstOutside} />
      ))}
      {emptyMessage && <span className={styles.emptyMsg}>{emptyMessage}</span>}
      <NewTabButton manageLocked={ctx.manageLocked} onNew={onNewTab} />
    </div>
  );
}

/**
 * The strip's rows between ‹ and ›: the buttons OUTSIDE the tablist (they are not seats), shown only while seats
 * are hidden, each disabled at its end. One seat per press (the canvas's TurnStrip artboard). The same elements
 * always - the buttons are hidden, never unmounted - so the tablist is never made anew when the strip overflows.
 */
function StripScroller({ strip, children }: { strip: ReturnType<typeof useStripOverflow>; children: ReactNode }) {
  const { state, step, frameRef } = strip;
  return (
    <div ref={frameRef} className={styles.stripScroller}>
      <button
        type="button"
        className={styles.stripStep}
        aria-label="Earlier seats"
        data-testid="turn-strip-back"
        hidden={!state.overflow}
        disabled={state.atStart}
        onClick={() => step(-1)}
      >
        ‹
      </button>
      {children}
      <button
        type="button"
        className={styles.stripStep}
        aria-label="Later seats"
        data-testid="turn-strip-forward"
        hidden={!state.overflow}
        disabled={state.atEnd}
        onClick={() => step(1)}
      >
        ›
      </button>
    </div>
  );
}

/**
 * The row handlers, the same functions every render (useStableHandlers): the rows are memoised, and the shell's
 * handlers they wrap are new on most renders, so every row re-rendered with each of the table's (2026-10-30).
 */
function useRowHandlers(p: OrderRailProps, targets: TargetsApi, participants: Participants): RowContext['handlers'] {
  const { locked, onRemoveParticipant, onHoverRow } = p;
  const { setTabCtx, setRenamingTab } = targets;
  const drag = useRailDrag(participants, targets, p.onReorderParticipants);
  const onKeyNav = useRailKeys(p.rows, targets.activeTab, targets.setActiveTab);
  const onContext = useCallback(
    (row: RailRow, e: MouseEvent) => {
      // The roster's menu (Rename, Act as, Duplicate, Track, Close) is the
      // shell's; a custom participant has no roster entry, so its
      // right-click is the chip's old one: remove.
      if (row.combatantId) setTabCtx({ id: row.combatantId, x: e.clientX, y: e.clientY });
      else if (row.participantId && !locked) onRemoveParticipant(row.participantId);
    },
    [setTabCtx, onRemoveParticipant, locked],
  );
  return useStableHandlers({
    onSelect: targets.setActiveTab,
    onKeyNav,
    onRenameStart: setRenamingTab,
    onRenameCommit: targets.handleRenameTab,
    onRenameCancel: () => setRenamingTab(null),
    onContext,
    onClose: targets.handleCloseTab,
    onSetInitiative: p.onSetInitiative,
    onReroll: p.onReroll,
    ...drag,
    onHover: (id: string | null) => onHoverRow?.(id),
  });
}

/** Everything a row needs from the rail: what is selected, hovered, locked, and the handlers. */
function useRowContext(
  p: OrderRailProps,
  targets: TargetsApi,
  view: { manageLocked: boolean; facts: ReturnType<typeof orderFacts> },
): RowContext {
  const handlers = useRowHandlers(p, targets, view.facts.participants);
  return {
    activeTab: targets.activeTab,
    hoveredId: p.hoveredId ?? null,
    locked: p.locked,
    manageLocked: view.manageLocked,
    orderDraggable: view.facts.orderDraggable,
    renamingTab: targets.renamingTab,
    handlers,
  };
}

type BodyProps = OrderRailProps & {
  facts: ReturnType<typeof orderFacts>;
  custom: CustomDraft;
  rowsNode: ReactNode;
};

/** The notes under the head: "manual order", and the join hint while the arrivals' rolls are fresh. */
function OrderNotes({ p }: { p: BodyProps }) {
  const { facts, locked } = p;
  return (
    <>
      {facts.manual && <ManualOrderNote locked={locked} onSetOrderMode={p.onSetOrderMode} />}
      {facts.showJoinHint && !locked && (
        <JoinHint joinedCount={facts.joinedCount} onRerollOthers={p.onRerollOthers} onDismiss={p.onDismissJoinHint} />
      )}
    </>
  );
}

/**
 * The strip above the map (plan design-gaps, L2), in its DOM order: the head, the notes, the rows, the END TURN
 * foot; the stylesheet lays it out left to right (ORDER, the seats, the notes, the controls and the round, END TURN -
 * the foot stays, because a viewer acting as nobody, the GM running the NPCs, has no turn card over the map). The
 * column beside the map, with its spine and the turn card under a row, went with the classic layout (v12.0.0).
 */
function RailBody(p: BodyProps) {
  const { trackerState, locked, facts, rowsNode } = p;
  // The kill-teams' Cohesion rides at the head, after ORDER, tracker on or off (Squad Mode, 2026-10-23).
  const cap = (
    <>
      <span className={styles.cap}>ORDER</span>
      <CohesionMeters />
    </>
  );
  if (!trackerState.active) {
    return (
      <RailFrame>
        {cap}
        {rowsNode}
      </RailFrame>
    );
  }
  return (
    <RailFrame>
      <div className={clsx(styles.trackerBar, locked && styles.locked)}>
        {cap}
        <TrackerHead {...p} hasParticipants={facts.hasParticipants} onTrackEveryone={p.onTrackEveryone ?? null} />
        <OrderNotes p={p} />
        {rowsNode}
        <EndTurnFoot
          locked={locked}
          hasParticipants={facts.hasParticipants}
          endSuggested={p.endSuggested ?? false}
          onEndTurn={p.onEndTurn}
        />
      </div>
    </RailFrame>
  );
}

/** The hooks and the rows, then the strip. */
function OrderRail(props: OrderRailProps) {
  const { rows, trackerState, locked, onTrackEveryone = null } = props;
  const targets = useTargets() as unknown as TargetsApi;
  const { isLocked } = useSession();
  const facts = orderFacts(trackerState, locked);
  const custom = useCustomDraft(props.onAddCustom);
  const ctx = useRowContext(props, targets, { manageLocked: isLocked('manageTabs'), facts });
  // The strip scrolls sideways past its width, keeping the acting seat in view.
  const stripScroll = useStripOverflow(targets.activeTab, actingRow(rows)?.key ?? null);
  const rowsNode = (
    <StripScroller strip={stripScroll}>
      <RailRows
        rows={rows}
        ctx={ctx}
        active={trackerState.active}
        emptyMessage={emptyMessage(trackerState.active, facts.hasParticipants, !!onTrackEveryone)}
        onNewTab={targets.handleNewTab}
        scroll={stripScroll}
      />
    </StripScroller>
  );
  return <RailBody {...props} facts={facts} custom={custom} rowsNode={rowsNode} />;
}

export default memo(OrderRail);
